
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface BoardActivity {
  id: string;
  board_id: string;
  user_id: string;
  user_name: string;
  activity_type: string;
  entity_type: string | null;
  entity_id: string | null;
  details: any;
  created_at: string;
}

interface UseActivityStreamConfig {
  boardId: string;
  limit?: number;
}

export function useActivityStream({ boardId, limit = 20 }: UseActivityStreamConfig) {
  const [activities, setActivities] = useState<BoardActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!boardId) {
      setIsLoading(false);
      return;
    }

    // Fetch initial activities
    const fetchActivities = async () => {
      try {
        const { data, error } = await supabase
          .from('board_activities')
          .select('*')
          .eq('board_id', boardId)
          .order('created_at', { ascending: false })
          .limit(limit);

        if (error) {
          console.error('❌ [ACTIVITY STREAM] Error fetching activities:', error);
          return;
        }

        setActivities(data || []);
      } catch (error) {
        console.error('❌ [ACTIVITY STREAM] Unexpected error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchActivities();

    // Subscribe to new activities
    const channel = supabase
      .channel(`board_activities_${boardId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'board_activities',
          filter: `board_id=eq.${boardId}`
        },
        (payload) => {
          console.log('📝 [ACTIVITY STREAM] New activity:', payload);
          const newActivity = payload.new as BoardActivity;
          setActivities(prev => [newActivity, ...prev.slice(0, limit - 1)]);
        }
      )
      .subscribe((status) => {
        console.log('📝 [ACTIVITY STREAM] Channel status:', status);
        setIsConnected(status === 'SUBSCRIBED');
      });

    return () => {
      supabase.removeChannel(channel);
      setIsConnected(false);
    };
  }, [boardId, limit]);

  const logActivity = async (
    activityType: string,
    entityType?: string,
    entityId?: string,
    details?: any
  ) => {
    try {
      const { error } = await supabase
        .from('board_activities')
        .insert({
          board_id: boardId,
          user_id: (await supabase.auth.getUser()).data.user?.id,
          user_name: (await supabase.auth.getUser()).data.user?.email || 'Anonymous',
          activity_type: activityType,
          entity_type: entityType || null,
          entity_id: entityId || null,
          details: details || {}
        });

      if (error) {
        console.error('❌ [ACTIVITY STREAM] Error logging activity:', error);
      }
    } catch (error) {
      console.error('❌ [ACTIVITY STREAM] Unexpected error:', error);
    }
  };

  return {
    activities,
    isLoading,
    isConnected,
    logActivity
  };
}
